import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { pedir } from '../api/cliente';
import { Selector } from './ui/formulario';

interface TurnoDeLaLista {
  readonly id: string;
  readonly puestoDeCajaId: string;
  readonly puesto?: string;
  readonly estado: 'abierto' | 'cerrado';
}

/** Una caja con su turno abierto: las únicas por las que el efectivo puede pasar. */
export interface CajaAbierta {
  readonly puestoDeCajaId: string;
  readonly nombre: string;
}

/** La caja que ya está puesta, cuando el pago o el cobro se hacen desde la pantalla de caja. */
export interface CajaFija {
  readonly puestoDeCajaId: string;
  readonly nombre: string;
}

/**
 * Las cajas con turno abierto (E37).
 *
 * Quien no puede ver los turnos recibe una lista vacía y paga fuera de caja, que es lo que pasaba
 * siempre: el servidor lo acepta, y la pantalla lo dice.
 */
export function useCajasAbiertas(habilitada: boolean): readonly CajaAbierta[] {
  const turnos = useQuery({
    queryKey: ['turnos-de-caja'],
    enabled: habilitada,
    queryFn: () => pedir<TurnoDeLaLista[]>('/turnos-de-caja'),
  });
  return (turnos.data ?? [])
    .filter((turno) => turno.estado === 'abierto')
    .map((turno) => ({ puestoDeCajaId: turno.puestoDeCajaId, nombre: turno.puesto ?? '—' }));
}

/**
 * Por qué caja pasa el efectivo: la puesta, la elegida, o la única abierta.
 *
 * Con una sola abierta no se pregunta: es la que es. Con varias, hay que elegir, y mientras no se
 * elija devuelve vacío para que la pantalla no deje confirmar.
 */
export function cajaDelEfectivo(
  fija: CajaFija | undefined,
  elegida: string,
  abiertas: readonly CajaAbierta[],
): string {
  if (fija !== undefined) return fija.puestoDeCajaId;
  if (elegida !== '') return elegida;
  return abiertas.length === 1 ? (abiertas[0]?.puestoDeCajaId ?? '') : '';
}

/**
 * Pide la caja cuando se paga o se cobra en efectivo (E37).
 *
 * Es el arreglo de un agujero que se veía al cerrar el turno: el pago en efectivo salía de los
 * libros y no del cajón, y el arqueo lo encontraba como faltante —que se asentaba otra vez—.
 */
export function CajaDelEfectivo({
  fija,
  abiertas,
  valor,
  onCambiar,
}: {
  readonly fija?: CajaFija | undefined;
  readonly abiertas: readonly CajaAbierta[];
  readonly valor: string;
  readonly onCambiar: (puestoDeCajaId: string) => void;
}): React.ReactElement {
  const { t } = useTranslation();

  if (fija !== undefined) {
    return (
      <p className="text-xs text-texto-tenue">
        {t('cajaDelEfectivo.fija', { puesto: fija.nombre })}
      </p>
    );
  }
  if (abiertas.length === 0) {
    return (
      <p role="status" className="text-xs text-atencion">
        {t('cajaDelEfectivo.sinCajas')}
      </p>
    );
  }
  return (
    <Selector
      etiqueta={t('cajaDelEfectivo.etiqueta')}
      obligatorio
      ayuda={t('cajaDelEfectivo.ayuda')}
      value={cajaDelEfectivo(undefined, valor, abiertas)}
      onChange={(evento) => {
        onCambiar(evento.target.value);
      }}
    >
      {abiertas.length > 1 ? <option value="">{t('cajaDelEfectivo.elegir')}</option> : null}
      {abiertas.map((caja) => (
        <option key={caja.puestoDeCajaId} value={caja.puestoDeCajaId}>
          {caja.nombre}
        </option>
      ))}
    </Selector>
  );
}
